import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import {
  ClipboardList, Send, XCircle, AlertTriangle, Users, Layers, ChevronDown, ChevronUp,
} from 'lucide-react';
import { PageHeader, Button, Modal, StatusPill, Select } from '../../components';
import { agencyService } from '../../services/agencyService';
import {
  CARD, inputClass, FieldLabel, FilterTabs, EmptyCard, ReasonModal, ShiftLine, BookingPill,
  errorMessage, money, plural, Segmented, dayLabel, timeRange, agencyCanCancel, CancelBookingModal,
} from './shared';

/**
 * Agency > Requests — cover the connected care homes asked this agency for.
 *
 * For each dated shift the agency either offers named staff with an hourly
 * charge, or says it can't cover it. The home then confirms (or turns down)
 * each named person; a confirmed one is a booking. Offers can be withdrawn
 * until the home confirms them.
 *
 * Each staff member's blockers come from the API — not approved at this home,
 * a role mismatch, missing proof, not available, already booked — so the
 * offer form can only send people the home could actually confirm.
 */

const OfferModal = ({ request, onClose, onSent }) => {
  const [candidates, setCandidates] = useState(null);
  const [places, setPlaces] = useState(0);
  const [chosen, setChosen] = useState([]);
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!request) return;
    setChosen([]); setRate(''); setNote(''); setError(''); setCandidates(null);
    agencyService.candidates(request.id)
      .then((res) => { setCandidates(res.data || []); setPlaces(res.meta?.open_places || 0); })
      .catch((err) => setError(errorMessage(err)));
  }, [request]);

  if (!request) return null;
  const pending = request.bookings.filter((b) => b.status === 'offered').length;
  const room = Math.max(0, places - pending);

  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length < room ? [...c, id] : c));

  const send = async () => {
    if (!chosen.length) { setError('Choose who to offer.'); return; }
    if (!(Number(rate) > 0)) { setError('Enter the hourly charge.'); return; }
    setBusy(true); setError('');
    try {
      const res = await agencyService.offer(request.id, { worker_ids: chosen, hourly_rate: Number(rate), note });
      toast.success(res.message);
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Offer staff · ${request.reference}`}
      maxWidth="max-w-xl"
      footer={(
        <div className="flex justify-between items-center gap-2">
          <span className="text-[12.5px] text-[#64748B]">{chosen.length} of {room} place{room === 1 ? '' : 's'} chosen</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button leftIcon={<Send className="w-4 h-4" />} onClick={send} loading={busy} disabled={!room}>Send offer</Button>
          </div>
        </div>
      )}
    >
      <ShiftLine item={request} place={request.home?.name} />
      {request.note && <p className="mt-2 text-[13px] text-[#475569] dark:text-gray-300">Note from the home: {request.note}</p>}
      {!room && <p className="mt-3 text-[13px] text-amber-700">Every open place already has an offer from you waiting for the home.</p>}

      <p className="mt-4 mb-2 text-[12px] font-semibold text-[#334155] dark:text-gray-300">Your staff</p>
      {!candidates && !error && <p className="text-[13px] text-[#94A3B8]">Loading…</p>}
      {candidates && !candidates.length && <p className="text-[13px] text-[#94A3B8]">You have no active staff yet. Add them under Staff.</p>}
      <ul className="flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
        {(candidates || []).map((c) => {
          const blocked = c.blockers.length > 0;
          const on = chosen.includes(c.id);
          return (
            <li key={c.id}>
              <label className={`flex items-start gap-3 p-3 rounded-xl border ${on ? 'border-blue-400 bg-[#F3F7FF] dark:bg-blue-500/10' : 'border-[#E3E9F3] dark:border-gray-800'} ${blocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input type="checkbox" className="mt-1" disabled={blocked} checked={on} onChange={() => toggle(c.id)} />
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">{c.name} <span className="font-normal text-[#94A3B8]">· {c.job_role} · {c.user_id}</span></p>
                  {blocked && (
                    <p className="mt-0.5 text-[12px] text-rose-600 flex items-start gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0" />{c.blockers[0]}
                      {c.blockers.length > 1 && <span className="text-[#94A3B8]">(+{c.blockers.length - 1} more)</span>}
                    </p>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div>
          <FieldLabel required>Hourly charge (£)</FieldLabel>
          <input className={inputClass} type="number" min="0.01" max="999" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Note to the home</FieldLabel>
          <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </div>
      </div>
      {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}
    </Modal>
  );
};

/**
 * Offer the same staff for every date of a group request at once. Each worker
 * shows how many of the dates they can work; the server offers them on those
 * dates only (in the order chosen, up to each date's open places) and says
 * which dates it left out for whom.
 */
const GroupOfferModal = ({ items, onClose, onSent }) => {
  const [candidates, setCandidates] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const first = items[0];

  useEffect(() => {
    setChosen([]); setRate(''); setNote(''); setError(''); setCandidates(null);
    agencyService.groupCandidates(items.map((r) => r.id))
      .then((res) => { setCandidates(res.data || []); setShifts(res.meta?.shifts || []); })
      .catch((err) => setError(errorMessage(err)));
  }, [items]);

  // A date takes as many of the chosen as it has places; offering more people
  // than the busiest date has room for would only ever be left out.
  const room = shifts.reduce((m, s) => Math.max(m, s.room), 0);
  const dayOf = useMemo(() => new Map(shifts.map((s) => [String(s.id), dayLabel(s.shift_date)])), [shifts]);
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length < room ? [...c, id] : c));

  const send = async () => {
    if (!chosen.length) { setError('Choose who to offer.'); return; }
    if (!(Number(rate) > 0)) { setError('Enter the hourly charge.'); return; }
    setBusy(true); setError('');
    try {
      const res = await agencyService.offerGroup({
        request_ids: items.map((r) => r.id), worker_ids: chosen, hourly_rate: Number(rate), note,
      });
      toast.success(res.message);
      const skipped = res.data?.skipped || [];
      if (skipped.length) {
        toast(`Left out:\n${skipped.slice(0, 4).join('\n')}${skipped.length > 4 ? `\n…and ${skipped.length - 4} more` : ''}`, { icon: '⚠️', duration: 9000 });
      }
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Offer staff · ${first.group_ref}`}
      maxWidth="max-w-xl"
      footer={(
        <div className="flex justify-between items-center gap-2">
          <span className="text-[12.5px] text-[#64748B]">{chosen.length} of {room} chosen · {plural(shifts.length, 'date')}</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button leftIcon={<Send className="w-4 h-4" />} onClick={send} loading={busy} disabled={!room}>Offer for all dates</Button>
          </div>
        </div>
      )}
    >
      <div className="min-w-0">
        <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">
          {plural(items.length, 'date')} · {timeRange(first)}
        </p>
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 truncate">{[first.home?.name, first.job_role].filter(Boolean).join(' · ')}</p>
      </div>
      {candidates && (
        <p className="mt-2 text-[12.5px] text-[#475569] dark:text-gray-300">
          {shifts.length
            ? <>Open to offer: {shifts.map((s) => dayLabel(s.shift_date)).join(', ')}.</>
            : 'Every date already has your offers waiting, or is covered.'}
        </p>
      )}
      {first.note && <p className="mt-2 text-[13px] text-[#475569] dark:text-gray-300">Note from the home: {first.note}</p>}

      <p className="mt-4 mb-2 text-[12px] font-semibold text-[#334155] dark:text-gray-300">Your staff</p>
      {!candidates && !error && <p className="text-[13px] text-[#94A3B8]">Loading…</p>}
      {candidates && !candidates.length && <p className="text-[13px] text-[#94A3B8]">You have no active staff yet. Add them under Staff.</p>}
      <ul className="flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
        {(candidates || []).map((c) => {
          const none = c.available === 0;
          const on = chosen.includes(c.id);
          const blocked = Object.entries(c.blocked || {});
          return (
            <li key={c.id}>
              <label className={`flex items-start gap-3 p-3 rounded-xl border ${on ? 'border-blue-400 bg-[#F3F7FF] dark:bg-blue-500/10' : 'border-[#E3E9F3] dark:border-gray-800'} ${none ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input type="checkbox" className="mt-1" disabled={none} checked={on} onChange={() => toggle(c.id)} />
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">{c.name} <span className="font-normal text-[#94A3B8]">· {c.job_role} · {c.user_id}</span></p>
                  {!blocked.length ? (
                    <p className="mt-0.5 text-[12px] text-emerald-700 dark:text-emerald-400">Can work all {plural(shifts.length, 'date')}</p>
                  ) : (
                    <div className={`mt-0.5 text-[12px] ${none ? 'text-rose-600' : 'text-amber-700 dark:text-amber-400'}`}>
                      <p className="flex items-start gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                        {none ? `Can't work any of these dates — ${blocked[0][1]}` : `Can work ${c.available} of ${shifts.length} dates`}
                      </p>
                      {!none && (
                        <ul className="pl-[18px] text-[#64748B] dark:text-gray-400">
                          {blocked.slice(0, 3).map(([id, why]) => <li key={id}>{dayOf.get(String(id))}: {why}</li>)}
                          {blocked.length > 3 && <li>…and {blocked.length - 3} more</li>}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div>
          <FieldLabel required>Hourly charge (£)</FieldLabel>
          <input className={inputClass} type="number" min="0.01" max="999" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Note to the home</FieldLabel>
          <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </div>
      </div>
      {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}
    </Modal>
  );
};

/** Where one date of a group stands for this agency — the chip colour + word. */
const dateState = (r) => {
  const mine = r.bookings;
  // Same colours as the status pills: Completed green, Did not attend / Missed
  // red, Confirmed brand blue. Offered is the dashed blue outline the Rota page
  // uses, so it can't be mistaken for Confirmed.
  if (mine.some((b) => b.state === 'no_show')) return { label: 'Did not attend', cls: 'bg-[#FFF1F3] text-[#E11D48] border-[#FFDCE3] dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50' };
  if (mine.some((b) => b.state === 'missed')) return { label: 'Missed', cls: 'bg-[#FFF1F3] text-[#E11D48] border-[#FFDCE3] dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50' };
  if (mine.some((b) => b.state === 'completed')) return { label: 'Completed', cls: 'bg-[#ECFDF5] text-[#059669] border-[#CFF3E4] dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-900/50' };
  if (mine.some((b) => b.status === 'confirmed')) return { label: 'Confirmed', cls: 'bg-[#EEF3FF] text-[#2563EB] border-[#D8E4FF] dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50' };
  if (mine.some((b) => b.status === 'offered')) return { label: 'Offered', cls: 'bg-white text-[#2563EB] border-dashed border-blue-300 dark:bg-transparent dark:text-blue-300 dark:border-blue-500/50' };
  if (r.declined) return { label: "Can't cover", cls: 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-white/5 dark:text-gray-400 dark:border-gray-700' };
  if (r.open_places > 0) return { label: 'Open', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30' };
  return { label: 'Covered', cls: 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-white/5 dark:text-gray-400 dark:border-gray-700' };
};

/**
 * Several dates the home asked for together (one CG- reference, same times):
 * one card, one "Offer staff for all dates", and the dates underneath for
 * anything that needs doing on a single date.
 */
const GroupCard = ({ items, onOfferGroup, onOffer, onDecline, onWithdraw, onCancelBooking }) => {
  const [expanded, setExpanded] = useState(false);
  const first = items[0];
  const last = items[items.length - 1];
  const open = items.reduce((t, r) => t + (r.ended ? 0 : r.open_places), 0);
  const canAct = items.some((r) => r.open_places > 0 && !r.ended);
  return (
    <div className={`${CARD} p-5 flex flex-col gap-3 xl:col-span-2`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">
            {dayLabel(first.shift_date)} – {dayLabel(last.shift_date)} · {timeRange(first)}
          </p>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 truncate">
            {[first.home?.name, first.job_role, plural(items.length, 'date')].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {open > 0 ? <StatusPill tone="warn">{plural(open, 'place')} open</StatusPill> : <StatusPill tone="good">Covered</StatusPill>}
          <span className="text-[11.5px] text-[#94A3B8]">{first.group_ref} · group request</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((r) => {
          const st = dateState(r);
          return (
            <span key={r.id} title={`${r.reference} · ${st.label}`} className={`inline-flex items-center gap-1 h-6 px-2 rounded-md border text-[11.5px] font-semibold ${st.cls}`}>
              {dayLabel(r.shift_date)}
            </span>
          );
        })}
      </div>
      {first.note && <p className="text-[13px] text-[#475569] dark:text-gray-300">“{first.note}”</p>}
      <div className="flex flex-wrap gap-2 pt-1">
        {canAct && <Button size="sm" leftIcon={<Users className="w-4 h-4" />} onClick={() => onOfferGroup(items)}>Offer staff for all dates</Button>}
        <Button size="sm" variant="secondary" rightIcon={expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} onClick={() => setExpanded((x) => !x)}>
          {expanded ? 'Hide dates' : 'Show dates'}
        </Button>
      </div>
      {expanded && (
        <ul className="flex flex-col divide-y divide-[#EEF2F6] dark:divide-gray-800 border-t border-[#EEF2F6] dark:border-gray-800">
          {items.map((r) => {
            const st = dateState(r);
            const offered = r.bookings.filter((b) => b.status === 'offered');
            const people = r.bookings.filter((b) => ['offered', 'confirmed', 'no_show'].includes(b.status));
            const dateCanAct = r.open_places > 0 && !r.ended;
            return (
              <li key={r.id} className="py-2.5 flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">
                    {dayLabel(r.shift_date)} <span className="font-normal text-[#94A3B8]">· {r.reference}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {/* A date with people on it is described by their own pills
                        underneath — repeating "Confirmed" here showed it twice. */}
                    {!people.length && (
                      <span className={`h-6 px-2 inline-flex items-center rounded-md border text-[11.5px] font-semibold ${st.cls}`}>{st.label}</span>
                    )}
                    {dateCanAct && <Button size="sm" variant="secondary" onClick={() => onOffer(r)}>Offer</Button>}
                    {dateCanAct && !r.declined && !offered.length && (
                      <Button size="sm" variant="ghost" onClick={() => onDecline(r)}>Can't cover</Button>
                    )}
                  </span>
                </div>
                {people.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 text-[12.5px] pl-3">
                    <span className="truncate text-[#334155] dark:text-gray-300">{b.worker?.name} <span className="text-[#94A3B8]">· {money(b.hourly_rate)}/h</span></span>
                    <span className="flex items-center gap-2">
                      <BookingPill booking={b} />
                      <PersonAction b={{ ...b, home: r.home }} onWithdraw={onWithdraw} onCancelBooking={onCancelBooking} />
                    </span>
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/** Withdraw (an offer) or Cancel (a confirmed booking) beside a person's pill. */
const PersonAction = ({ b, onWithdraw, onCancelBooking }) => {
  if (b.status === 'offered') {
    return <button type="button" className="text-[12px] font-semibold text-rose-600 hover:underline" onClick={() => onWithdraw(b)}>Withdraw</button>;
  }
  if (agencyCanCancel(b)) {
    return <button type="button" className="text-[12px] font-semibold text-rose-600 hover:underline" onClick={() => onCancelBooking(b)}>Cancel</button>;
  }
  return null;
};

const RequestCard = ({ r, onOffer, onDecline, onWithdraw, onCancelBooking }) => {
  const mine = r.bookings;
  const offered = mine.filter((b) => b.status === 'offered');
  const confirmed = mine.filter((b) => ['confirmed', 'no_show'].includes(b.status));
  const canAct = r.open_places > 0 && !r.ended;
  return (
    <div className={`${CARD} p-5 flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-3">
        <ShiftLine item={r} place={r.home?.name} />
        <div className="flex flex-col items-end gap-1 shrink-0">
          {r.declined ? <StatusPill tone="mute">You can't cover</StatusPill>
            : r.open_places > 0 ? <StatusPill tone="warn">{plural(r.open_places, 'place')} open</StatusPill>
              : <StatusPill tone="good">Covered</StatusPill>}
          <span className="text-[11.5px] text-[#94A3B8]">{r.reference}{r.group_ref ? ` · ${r.group_ref}` : ''}</span>
        </div>
      </div>
      {r.note && <p className="text-[13px] text-[#475569] dark:text-gray-300">“{r.note}”</p>}
      {(offered.length > 0 || confirmed.length > 0) && (
        <ul className="flex flex-col gap-1.5">
          {[...confirmed, ...offered].map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="truncate text-[#0B1220] dark:text-gray-100">{b.worker?.name} <span className="text-[#94A3B8]">· {money(b.hourly_rate)}/h</span></span>
              <span className="flex items-center gap-2">
                <BookingPill booking={b} />
                <PersonAction b={{ ...b, home: r.home }} onWithdraw={onWithdraw} onCancelBooking={onCancelBooking} />
              </span>
            </li>
          ))}
        </ul>
      )}
      {canAct && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" leftIcon={<Users className="w-4 h-4" />} onClick={() => onOffer(r)}>Offer staff</Button>
          {!r.declined && !offered.length && (
            <Button size="sm" variant="secondary" leftIcon={<XCircle className="w-4 h-4" />} onClick={() => onDecline(r)}>Can't cover</Button>
          )}
        </div>
      )}
    </div>
  );
};

const AgencyRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('open');
  const [filter, setFilter] = useState('respond');
  const [homeId, setHomeId] = useState('');
  const [offering, setOffering] = useState(null);
  const [offeringGroup, setOfferingGroup] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await agencyService.requests({ view });
      setRows(res.data || []);
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load requests.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);
  // A home posting, cancelling or confirming shows here at once. The filter
  // tab, care-home filter, expanded groups and any open offer form are kept.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const homes = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => r.home && map.set(r.home.id, r.home.name));
    return [...map].map(([value, label]) => ({ value: String(value), label }));
  }, [rows]);

  const scoped = rows.filter((r) => !homeId || String(r.home?.id) === homeId);
  const preds = {
    respond: (r) => r.open_places > 0 && !r.declined && !r.bookings.some((b) => b.status === 'offered'),
    offered: (r) => r.bookings.some((b) => b.status === 'offered'),
    covered: (r) => r.bookings.some((b) => ['confirmed', 'no_show'].includes(b.status)),
    all: () => true,
  };
  const list = view === 'past' ? scoped : scoped.filter(preds[filter]);

  // Dates the home asked for together (same CG- reference, same times) become
  // one card. A single date of a group, or a group the home edited to mixed
  // times, still shows as its own card(s).
  const cards = useMemo(() => {
    const keyOf = (r) => (r.group_ref ? `${r.group_ref}|${r.start_time}|${r.end_time}|${r.job_role}` : null);
    const groups = new Map();
    list.forEach((r) => { const k = keyOf(r); if (k) groups.set(k, [...(groups.get(k) || []), r]); });
    const placed = new Set();
    const out = [];
    list.forEach((r) => {
      const k = keyOf(r);
      const members = k ? groups.get(k) : null;
      if (members && members.length > 1) {
        if (!placed.has(k)) { placed.add(k); out.push({ key: k, group: members }); }
      } else {
        out.push({ key: `r${r.id}`, request: r });
      }
    });
    return out;
  }, [list]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Requests"
        subtitle="Cover your connected care homes have asked you for. Offer named staff with your hourly charge — the home confirms each person."
        actions={<Segmented value={view} onChange={setView} options={[{ value: 'open', label: 'Open' }, { value: 'past', label: 'Past 60 days' }]} />}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        {view === 'open' ? (
          <FilterTabs
            value={filter}
            onChange={setFilter}
            tabs={[
              { value: 'respond', label: 'Needs your response', count: scoped.filter(preds.respond).length, alert: true },
              { value: 'offered', label: 'Awaiting the home', count: scoped.filter(preds.offered).length },
              { value: 'covered', label: 'You have staff booked', count: scoped.filter(preds.covered).length },
              { value: 'all', label: 'All', count: scoped.length },
            ]}
          />
        ) : <span />}
        {homes.length > 1 && (
          <Select
            value={homeId}
            onChange={(v) => setHomeId(v)}
            options={[{ value: '', label: 'All care homes' }, ...homes]}
            className="w-full sm:w-64"
          />
        )}
      </div>

      {!loading && !list.length && (
        <EmptyCard
          icon={view === 'past' ? Layers : ClipboardList}
          title={view === 'past' ? 'No past requests' : 'Nothing here right now'}
          text="New cover requests from your connected care homes appear here, and in your notifications."
        />
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {cards.map((c) => (c.group ? (
          <GroupCard key={c.key} items={c.group} onOfferGroup={setOfferingGroup} onOffer={setOffering} onDecline={setDeclining} onWithdraw={setWithdrawing} onCancelBooking={setCancelling} />
        ) : (
          <RequestCard key={c.key} r={c.request} onOffer={setOffering} onDecline={setDeclining} onWithdraw={setWithdrawing} onCancelBooking={setCancelling} />
        )))}
      </div>

      {offering && <OfferModal request={offering} onClose={() => setOffering(null)} onSent={() => { setOffering(null); load(); }} />}
      {offeringGroup && <GroupOfferModal items={offeringGroup} onClose={() => setOfferingGroup(null)} onSent={() => { setOfferingGroup(null); load(); }} />}
      <CancelBookingModal booking={cancelling} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); }} />

      <ReasonModal
        open={Boolean(declining)}
        title="Can't cover this shift"
        intro="The care home is told straight away, so it can look elsewhere."
        label="Reason (optional)"
        required={false}
        confirmLabel="Tell the home"
        onClose={() => setDeclining(null)}
        onSubmit={async (reason) => {
          const res = await agencyService.declineRequest(declining.id, reason);
          toast.success(res.message);
          setDeclining(null);
          load();
        }}
      />
      <ReasonModal
        open={Boolean(withdrawing)}
        title={`Withdraw ${withdrawing?.worker?.name || ''}`}
        intro="Only an offer the home has not confirmed yet can be withdrawn."
        confirmLabel="Withdraw offer"
        onClose={() => setWithdrawing(null)}
        onSubmit={async (reason) => {
          const res = await agencyService.withdrawOffer(withdrawing.id, reason);
          toast.success(res.message);
          setWithdrawing(null);
          load();
        }}
      />
    </div>
  );
};

export default AgencyRequests;
